import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link as RouterLink} from 'react-router';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    GlobalStyles,
    Grid,
    IconButton,
    Link,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowsRotate, faCode} from '@fortawesome/pro-solid-svg-icons';
import {EventCalendar} from '@mui/x-scheduler/event-calendar';
import {deDE} from '@mui/x-scheduler/locales';
import {de as deDateLocale} from 'date-fns/locale';
import dayjs, {Dayjs} from 'dayjs';

import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {
    CalendarAccessDto,
    CalendarEmployeeDto,
    CalendarViewDto,
    EUserGroup,
    OutlookCalendarViewDto,
} from 'apiClient/Api';

import {CalendarEventDetail, buildSources, toOutlookEvents, toSchedulerEvents} from './calendarSources';
import EventDetailDialog from './EventDetailDialog';
import {apiErrorMessage} from './errorText';

/** Die Ansichten des Kalenders. Entspricht `CalendarView` des Schedulers. */
type CalendarView = 'day' | 'week' | 'month' | 'agenda';

/**
 * Der eingebaute Termin-Dialog zeigt die Beschreibung als einfachen Text. Er ist nur noch der
 * Rückfall (siehe `findClickedEventId`), soll dann aber lesbar sein — mehrzeilige Inhalte liefen
 * sonst zu einer Wurst zusammen. Er hängt in einem Portal außerhalb dieser Seite, deshalb geht das
 * nur über eine globale Regel und nicht über `sx`.
 */
const dialogStyles = (
    <GlobalStyles styles={{
        '.MuiEventCalendar-eventDialogDescriptionLabel': {
            whiteSpace: 'pre-line',
        },
    }}/>
);

/**
 * Die Termin-Elemente des Kalenders in allen Ansichten (Zeitraster, Monatsraster, Agenda).
 * Nur der Titel-Knoten trägt einen erkennbaren Klassennamen, den wir zur Zuordnung brauchen.
 */
const EVENT_TITLE_SELECTOR = [
    '[class*="-timeGridEventTitle"]',
    '[class*="-dayGridEventTitle"]',
    '[class*="-eventItemTitle"]',
].join(',');

/**
 * Ermittelt, welcher Termin angeklickt wurde.
 *
 * **Warum über das DOM:** `@mui/x-scheduler` 9.0.0-beta.9 bietet keinen Klick-Hook an — die
 * öffentliche API kennt nur `setVisibleDate`, es gibt keine Slots und kein `onEventClick`. Ohne
 * diesen Umweg gäbe es keinen eigenen Dialog und damit keinen Knopf in die Teams-Besprechung.
 *
 * **Warum das vertretbar ist:** Findet sich kein Titel-Element oder passt der Titel zu keinem
 * bekannten Termin, gibt die Funktion `null` zurück — dann läuft der Klick unverändert weiter und
 * der eingebaute Dialog öffnet wie bisher. Ändert die Bibliothek ihre Klassennamen, verliert man
 * also Komfort, keine Funktion.
 */
const findClickedEventId = (
    target: HTMLElement,
    events: {id: string | number; title: string; start: string}[],
): string | null => {
    const card = target.closest('[class*="EventCalendar-"]');
    if (!card) return null;

    const titleNode = card.querySelector(EVENT_TITLE_SELECTOR)
        ?? target.closest(EVENT_TITLE_SELECTOR);
    const title = titleNode?.textContent?.trim();
    if (!title) return null;

    const matches = events.filter((e) => e.title.trim() === title);
    if (matches.length === 0) return null;

    // Mehrere Termine mit gleichem Titel im Blick: über die im Element stehende Uhrzeit
    // eingrenzen. Bleibt es mehrdeutig, gewinnt der erste — schlechter als exakt, aber besser
    // als kein Dialog.
    if (matches.length > 1) {
        const time = card.textContent?.match(/\b(\d{1,2}):(\d{2})\b/);
        if (time) {
            const wanted = `${time[1].padStart(2, '0')}:${time[2]}`;
            const exact = matches.find((e) => e.start.slice(11, 16) === wanted);
            if (exact) return String(exact.id);
        }
    }

    return String(matches[0].id);
};

/**
 * Wandelt den Datumstyp des Kalenders in dayjs.
 *
 * Der Scheduler gibt seinen internen Typ heraus (Date, ISO-Zeichenkette oder ein
 * Temporal-Objekt) — der Typ ist bewusst offen. dayjs versteht die ersten beiden Formen direkt,
 * für die dritte hilft der Umweg über die Zeichenkette.
 */
const toDayjs = (value: unknown): Dayjs => {
    const direct = dayjs(value as never);
    return direct.isValid() ? direct : dayjs(String(value));
};

/**
 * Der eigene Kalender.
 *
 * Die Person ist nie Teil der Anfrage — der Server leitet sie aus dem LabelBenutzer des
 * angemeldeten Kontos ab. Diese Seite kann deshalb keinen fremden Kalender anzeigen, und es gibt
 * hier bewusst keine Personenauswahl.
 *
 * Vor dem ersten Abruf wird `GET /Calendar/Me/Access` gefragt. Der antwortet immer mit 200 und
 * sagt in Klartext, ob und warum der Kalender nicht zur Verfügung steht ("kein LabelBenutzer
 * hinterlegt" ist der häufigste Fall und ein Zustand, kein Fehler) — so muss die Seite nicht erst
 * in ein 403 laufen, um das herauszufinden.
 *
 * Dargestellt wird mit dem `EventCalendar` aus MUI X. Die Herkunft der Termine steckt in dessen
 * Ressourcen-Baum (siehe `calendarSources.ts`), damit Labelwin und Microsoft 365 in einem Raster
 * liegen und trotzdem einzeln ein- und ausblendbar sind.
 */
export const MyCalendar: React.FC = () => {
    const {t, i18n} = useTranslation();
    useDocumentTitle('calendar.title');
    const context = useApplicationContext();

    const [access, setAccess] = useState<CalendarAccessDto | undefined>(undefined);
    const [accessLoading, setAccessLoading] = useState(true);
    const [view, setView] = useState<CalendarViewDto | undefined>(undefined);
    const [outlook, setOutlook] = useState<OutlookCalendarViewDto | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const [visibleDate, setVisibleDate] = useState<Dayjs>(() => dayjs().startOf('day'));
    const [calendarView, setCalendarView] = useState<CalendarView>('week');
    const [showRaw, setShowRaw] = useState(false);

    // Fremder Kalender: `null` heißt „mein eigener". Nur Administratoren können hier etwas wählen.
    const [person, setPerson] = useState<CalendarEmployeeDto | null>(null);
    const [employees, setEmployees] = useState<CalendarEmployeeDto[]>([]);

    /** Der Termin, dessen Detail-Dialog offen ist. */
    const [selected, setSelected] = useState<CalendarEventDetail | null>(null);

    const isAdmin = (context.userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.ADMIN;
    const isGerman = i18n.language.toLowerCase().startsWith('de');

    /**
     * Die Kennung, unter der eine Person adressiert wird. Für Labelwin ist beides gleichwertig
     * (dort wird über Mail, interne Mail und LabelBenutzer gesucht); für Outlook ist die externe
     * Adresse die bessere Wahl, weil sie im Haus dem Anmeldenamen in Microsoft 365 entspricht.
     */
    const personKey = person?.mail || person?.mailInternal || person?.labelUser || undefined;

    /*
     * Geholt wird immer der ganze Monat auf volle Wochen erweitert — unabhängig von der Ansicht.
     * Damit deckt eine Anfrage Tag, Woche, Monat und Agenda ab: das Umschalten der Ansicht und
     * das Blättern innerhalb des Monats kommen ohne neuen Abruf aus, und im Monatsraster sind die
     * angeschnittenen Tage der Nachbarmonate mit dabei.
     */
    const range = useMemo(() => {
        const first = visibleDate.startOf('month');
        const last = visibleDate.endOf('month').startOf('day');
        return {
            from: first.subtract((first.day() + 6) % 7, 'day'),
            to: last.add(6 - ((last.day() + 6) % 7), 'day'),
        };
    }, [visibleDate]);

    useEffect(() => {
        setAccessLoading(true);
        context.api.calendar
            .calendarGetMyAccess()
            .then((response) => setAccess(response.data))
            .catch(context.apiError)
            .finally(() => setAccessLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*
     * Beide Quellen werden **unabhängig** geholt. Fällt LabelConnect aus, sollen die
     * Outlook-Termine trotzdem erscheinen — und umgekehrt. Deshalb zwei Abrufe, zwei Zustände und
     * kein gemeinsames `catch`, das eine Quelle die andere mitnehmen lässt.
     */
    // Mitarbeiterliste für die Personenauswahl. Nur Administratoren dürfen den Endpunkt abrufen.
    useEffect(() => {
        if (!isAdmin) return;
        context.api.labelConnect
            .labelConnectGetEmployees()
            .then((response) => setEmployees(response.data))
            // Still: ohne Liste bleibt die Auswahl leer, der eigene Kalender funktioniert weiter.
            .catch(() => setEmployees([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const load = useCallback(() => {
        if (!access?.available) return;

        const von = range.from.format('YYYY-MM-DD');
        const bis = range.to.format('YYYY-MM-DD');

        setLoading(true);
        setError(undefined);

        const labelwin = (personKey
            ? context.api.calendar.calendarGetCalendarForUser({user: personKey, von, bis})
            : context.api.calendar.calendarGetMyCalendar({von, bis}))
            .then((response) => setView(response.data))
            .catch((e: any) => {
                // Die Anbindung kann zwischen Access-Abfrage und Abruf wegbrechen (503) — dann
                // gehört die Begründung des Servers auf die Seite, nicht in eine Snackbar.
                setError(apiErrorMessage(e, t('calendar.loadFailed')));
                setView(undefined);
            });

        // Der Outlook-Endpunkt antwortet immer mit 200 und begründet sich selbst; ein `catch`
        // deckt nur den Transportfehler ab und bleibt bewusst still.
        const microsoft = (personKey
            ? context.api.calendar.calendarGetOutlookCalendarForUser({user: personKey, von, bis})
            : context.api.calendar.calendarGetMyOutlookCalendar({von, bis}))
            .then((response) => setOutlook(response.data))
            .catch(() => setOutlook(undefined));

        Promise.all([labelwin, microsoft]).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [access?.available, range.from.valueOf(), range.to.valueOf(), personKey]);

    useEffect(() => {
        load();
    }, [load]);

    const outlookAvailable = outlook?.available ?? false;

    const {events, details} = useMemo(() => {
        const labelwin = toSchedulerEvents(view, t);
        const microsoft = toOutlookEvents(outlook, t);

        // forEach statt Spread: das Übersetzungsziel des Projekts kann Map-Iteratoren nicht
        // abbilden (dieselbe Einschränkung wie an anderen Stellen im Client).
        const merged = new Map<string, CalendarEventDetail>();
        labelwin.details.forEach((value, key) => merged.set(key, value));
        microsoft.details.forEach((value, key) => merged.set(key, value));

        return {events: [...labelwin.events, ...microsoft.events], details: merged};
    }, [view, outlook, t]);

    const resources = useMemo(() => buildSources(t, outlookAvailable), [t, outlookAvailable]);

    /**
     * Klick auf einen Termin: eigenen Dialog öffnen und den eingebauten unterdrücken.
     *
     * Läuft in der Erfassungsphase, damit der Kalender den Klick nicht zuerst sieht. Lässt sich der
     * Termin nicht zuordnen, wird nichts unterdrückt — dann öffnet wie bisher der eingebaute
     * Dialog, der dieselben Angaben als Text enthält.
     */
    const handleCalendarClickCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
        const id = findClickedEventId(
            event.target as HTMLElement,
            events.map((e) => ({id: e.id, title: e.title, start: e.start})));
        if (!id) return;

        const found = details.get(id);
        if (!found) return;

        event.preventDefault();
        event.stopPropagation();
        setSelected(found);
    };

    // Die deutschen Texte des Kalenders liegen als Theme-Ergänzung vor; hier wird nur der
    // Textteil entnommen, damit die Umstellung der Sprache ohne Theme-Neubau greift.
    const localeText = isGerman
        ? deDE.components.MuiEventCalendar.defaultProps.localeText
        : undefined;

    if (accessLoading) {
        return (
            <Grid size={12}>
                <Box sx={{display: 'flex', justifyContent: 'center', p: 6}}><CircularProgress/></Box>
            </Grid>
        );
    }

    if (!access?.available) {
        return (
            <Grid size={12} sx={{maxWidth: 900}}>
                <Typography variant="h5" gutterBottom>{t('calendar.title')}</Typography>
                <Alert severity={access?.hasLabelUser === false ? 'info' : 'warning'} sx={{mt: 2}}>
                    <AlertTitle>{t('calendar.unavailableTitle')}</AlertTitle>
                    {access?.message}
                    {access?.hasLabelUser === false && isAdmin && (
                        <Box sx={{mt: 1.5}}>
                            <Link component={RouterLink} to="/admin/users">{t('calendar.goToUsers')}</Link>
                        </Box>
                    )}
                    {access?.hasLabelUser === false && !isAdmin && (
                        <Box sx={{mt: 1.5}}>
                            <Typography variant="body2">{t('calendar.askAdmin')}</Typography>
                        </Box>
                    )}
                </Alert>
            </Grid>
        );
    }

    return (
        <Grid size={12}>
            {dialogStyles}

            {/* Kopf: wessen Kalender, Zustand, Neuladen. Blättern und Ansicht macht der Kalender selbst. */}
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5}
                   sx={{alignItems: {sm: 'center'}, justifyContent: 'space-between', mb: 2}}>
                <Box>
                    <Typography variant="h5">
                        {person ? t('calendar.titleForeign', {name: person.name}) : t('calendar.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('calendar.subtitle', {
                            name: (person ?? view?.employee ?? access.employee)?.name ?? '—',
                            employeeId: (person ?? view?.employee ?? access.employee)?.employeeId ?? '—',
                            labelUser: (person ?? view?.employee)?.labelUser ?? access.labelUser ?? '—',
                        })}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 1}}>
                    {/*
                      * Personenauswahl nur für Administratoren — der Server lässt den Fremdzugriff
                      * auch nur für sie zu und protokolliert jeden. Leeres Feld = eigener Kalender.
                      */}
                    {isAdmin && (
                        <Autocomplete
                            size="small"
                            sx={{minWidth: 260}}
                            options={employees}
                            value={person}
                            onChange={(_, value) => setPerson(value)}
                            getOptionLabel={(option) => option.name ?? option.labelUser ?? '—'}
                            isOptionEqualToValue={(a, b) => a.employeeId === b.employeeId}
                            renderInput={(params) => (
                                <TextField {...params} label={t('calendar.personLabel')}
                                           placeholder={t('calendar.personPlaceholder')}/>
                            )}
                        />
                    )}
                    {loading && <CircularProgress size={16}/>}
                    <Chip size="small" label={t('calendar.entryCount', {count: events.length})}/>
                    {!access.allowWrites && (
                        <Chip size="small" variant="outlined" label={t('calendar.readOnly')}/>
                    )}
                    {/*
                      * Fehlt Outlook, sagt der Server in Klartext warum. Als Chip mit Erklärung
                      * statt als Warnbalken: für Konten ohne Microsoft-Postfach ist das ein
                      * Dauerzustand, kein Vorfall.
                      */}
                    {outlook && !outlookAvailable && (
                        <Tooltip title={outlook.message ?? ''}>
                            <Chip size="small" variant="outlined" color="default"
                                  label={t('calendar.outlookUnavailable')}/>
                        </Tooltip>
                    )}
                    <Tooltip title={t('calendar.reload')}>
                        <IconButton size="small" onClick={load} disabled={loading}>
                            <FontAwesomeIcon icon={faArrowsRotate}/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError(undefined)}>{error}</Alert>}

            {/*
              * Der Kalender braucht eine feste Höhe — er verteilt seinen Inhalt selbst darauf und
              * scrollt intern. Ohne Höhe fällt er auf null zusammen.
              */}
            <Paper variant="outlined" sx={{overflow: 'hidden'}}
                   onClickCapture={handleCalendarClickCapture}>
                <EventCalendar
                    events={events}
                    resources={resources}
                    view={calendarView}
                    onViewChange={(next) => setCalendarView(next as CalendarView)}
                    visibleDate={visibleDate.toDate()}
                    onVisibleDateChange={(next) => setVisibleDate(toDayjs(next).startOf('day'))}
                    // Schreibzugriff hängt am Server-Schalter (LabelConnect.AllowWrites). Solange
                    // er aus ist, ist der ganze Kalender schreibgeschützt; danach entscheidet das
                    // `readOnly` je Termin, was Labelwin gehört und was nicht.
                    readOnly={!access.allowWrites}
                    eventCreation={access.allowWrites}
                    defaultPreferences={{
                        // 24-Stunden-Anzeige: die Vorgabe des Bausteins ist am/pm.
                        ampm: false,
                        showWeekNumber: true,
                    }}
                    localeText={localeText}
                    dateLocale={isGerman ? deDateLocale : undefined}
                    sx={{height: {xs: '70vh', md: 'calc(100vh - 260px)'}, minHeight: 520}}
                />
            </Paper>

            {/*
              * Ist eine andere Person gewählt, stammt der Termin aus deren Postfach — der Weblink
              * von Graph führt dorthin und ist im eigenen Outlook nicht zu öffnen.
              */}
            <EventDetailDialog detail={selected} ownMailbox={person === null}
                               onClose={() => setSelected(null)}/>

            {/*
              * Rohdaten der Antwort — Werkzeug für das Abnehmen der Anbindung gegen die Plantafel,
              * deshalb nur für Administratoren und bewusst unauffällig am Fuß der Seite.
              */}
            {view && isAdmin && (
                <Box sx={{mt: 1.5}}>
                    <Button size="small" color="inherit" onClick={() => setShowRaw(!showRaw)}
                            startIcon={<FontAwesomeIcon icon={faCode}/>}
                            sx={{color: 'text.secondary', textTransform: 'none'}}>
                        {t('calendar.showRaw')}
                    </Button>
                    <Collapse in={showRaw}>
                        <Paper variant="outlined" sx={{mt: 1, p: 1.5, overflowX: 'auto'}}>
                            <Typography component="pre" variant="caption"
                                        sx={{m: 0, fontFamily: 'monospace', whiteSpace: 'pre'}}>
                                {JSON.stringify(view, null, 2)}
                            </Typography>
                        </Paper>
                    </Collapse>
                </Box>
            )}
        </Grid>
    );
};

export default MyCalendar;
